import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, Plus, Minus } from "lucide-react";
import { AuthenticatedLayout } from "@/components/layout/AuthenticatedLayout";
import { useShoppingCart } from "@/hooks";
import { toast } from "@/components/ui/use-toast";

const CartPage: React.FC = () => {
  const navigate = useNavigate();

  // Use local storage shopping cart hook
  const {
    cartItems,
    cartTotals,
    removeFromCart,
    increaseQuantity,
    decreaseQuantity,
    checkout,
  } = useShoppingCart();

  // Set document title
  useEffect(() => {
    document.title = "Giỏ hàng | Locuno Marketplace";
  }, []);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleCheckout = async () => {
    const result = await checkout();
    if (result.success) {
      toast({
        title: "Đặt hàng thành công!",
        description: `Mã đơn hàng: ${result.orderId}`,
      });
      navigate("/marketplace");
    } else {
      toast({
        variant: "destructive",
        title: "Đặt hàng thất bại",
        description: result.error,
      });
    }
  };

  // Format price with commas and đ
  const formatPrice = (value: number) => {
    return new Intl.NumberFormat("vi-VN").format(value) + "đ";
  };

  return (
    <AuthenticatedLayout backgroundColor="#FBF6F1">
      <div className="pb-20 bg-[#FBF6F1]">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#FBF6F1] border-b border-gray-200">
          <div className="flex justify-between items-center px-4 py-3">
            <button
              className="p-1 rounded-full hover:bg-gray-100"
              onClick={handleGoBack}
            >
              <ArrowLeft className="h-5 w-5 text-[#1E3A3A]" />
            </button>
            <h1 className="text-lg font-medium text-[#1E3A3A]">Giỏ hàng</h1>
            <div className="w-5"></div> {/* Empty div for spacing */}
          </div>
        </div>

        {/* Cart Content */}
        <div className="px-4 py-4 bg-[#FBF6F1]">
          {cartItems.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[#6F6F6B] mb-4">Giỏ hàng của bạn đang trống</p>
              <button
                className="bg-[#E06448] text-white px-4 py-2 rounded-full"
                onClick={() => navigate("/marketplace")}
              >
                Tiếp tục mua sắm
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-4 mb-6">
                {cartItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex gap-3 border-b border-gray-100 pb-4"
                  >
                    <img
                      src={item.image || "https://placehold.co/80x80"}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded-md"
                    />
                    <div className="flex-1">
                      <h3 className="text-[#1E3A3A] font-medium">
                        {item.name}
                      </h3>
                      <p className="text-[#E06448]">
                        {formatPrice(item.price)}
                      </p>
                      <div className="flex justify-between items-center mt-2">
                        <div className="flex items-center border border-gray-300 rounded-md">
                          <button
                            className="px-2 py-1 text-[#1E3A3A] hover:bg-gray-100"
                            onClick={() => decreaseQuantity(item.id)}
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="px-2 py-1 min-w-[40px] text-center">
                            {item.quantity}
                          </span>
                          <button
                            className="px-2 py-1 text-[#1E3A3A] hover:bg-gray-100"
                            onClick={() => increaseQuantity(item.id)}
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                        <button
                          className="text-[#6F6F6B] hover:text-red-500"
                          onClick={() => removeFromCart(item.id)}
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-[#F4E7DA] p-4 rounded-lg">
                <div className="flex justify-between mb-2">
                  <span className="text-[#6F6F6B]">Tạm tính</span>
                  <span className="text-[#1E3A3A] font-medium">
                    {formatPrice(cartTotals.totalPrice)}
                  </span>
                </div>
                <div className="flex justify-between mb-4">
                  <span className="text-[#6F6F6B]">Phí vận chuyển</span>
                  <span className="text-[#1E3A3A] font-medium">0đ</span>
                </div>
                <div className="border-t border-gray-200 pt-2">
                  <div className="flex justify-between">
                    <span className="text-[#1E3A3A] font-medium">
                      Tổng cộng
                    </span>
                    <span className="text-[#E06448] font-medium">
                      {formatPrice(cartTotals.totalPrice)}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Bottom Action Bar */}
        {cartItems.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-[#FBF6F1] border-t border-gray-200 p-4">
            <button
              className="w-full bg-[#E06448] text-white py-3 rounded-full font-medium"
              onClick={handleCheckout}
            >
              Thanh toán ({formatPrice(cartTotals.totalPrice)})
            </button>
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  );
};

export default CartPage;
